import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, withRouter } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Footer } from "../component/layout/Footer";
import HeaderList from "../component/layout/HeaderList";

import ErrorBoundary from "../component/common/ErrorBoundry";
import { HandelError } from "../component/common/HandleError";
import { Loader } from "../component/common/Loader";
import { SWUpdate } from "../component/common/SWUpdate";
import { authenticate, logout } from "../redux/actions/Auth";
import { VRSConst } from "./Constants";

function App({ children, location }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const permission = useSelector((state) => state.AuthReducer.permission);
  const userDetails = useSelector((state) => state.AuthReducer.user);
  // Mobile user details (for servicer dashboard sync)
  const mobileUserDetails = useSelector((state) => state.AuthReducer.muser);
  const authenticated = useRef(false);
  // useEffect(() => {
  //   setToken(
  //     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCIsImV4cCI6IjEyMDAifQ.eyJDdXN0b21lcklEIjo3MCwiTG9nZ2VkSW5TdGFmZklEIjowLCJDdXN0b21lck5hbWUiOiJKaWxsIE1hc29uIiwiQ3JlYXRlRGF0ZVRpbWUiOiIyMDIyMDIyODA4MTUifQ.OUq-T1UGtdJxdxCkCkL75jMZYDLSNuUnFM_vNh5kUHs"
  //   );
  // }, []);
  const callAuthenticate = useCallback(async () => {
    try {
      // comment out the checking of valid key
      if (!authenticated.current) {
        setLoading(true);
        const res = await authenticate(dispatch);
        if (res) {
          setLoading(false);
        }
      }
    } catch (e) {
      console.log(e);
      setLoading(false);
      logout();
      toast.error(<HandelError error={e} />, { autoClose: 2000 });
    }
  }, [dispatch]);
  useEffect(() => {
    const removeLoader = () => {
      const loader = document.getElementById("parentLoader");
      if (loader) {
        loader.remove();
      }
    };
    removeLoader();
  }, []);
  useEffect(() => {
    /**
     * This method checks for the authentication of the user
     */
    if (!location.pathname.includes("servicer-dashboard")) {
      callAuthenticate();
    }
  }, [callAuthenticate, dispatch, location]);
  /**
   * Call the method for refresh token API call
   * @returns {Promise<void>}
   */
  const callRefreshToken = async () => {
    try {
      // await tokenRefresh();
    } catch (error) {
      toast.error(<HandelError error={error} />, { autoClose: 2000 });
    }
  };
  // schedule to call refresh token in every 55 minutes to refresh the access token
  useEffect(() => {
    const interval = setInterval(() => {
      // method call for refresh token
      callRefreshToken();
    }, VRSConst.refreshTime * 1000);
    return () => clearInterval(interval);
  }, []);

  // offline usage message and online sync
  React.useEffect(() => {
    const handleOnline = async () => {
      const timestamp = new Date().toISOString();
      console.log(`[${timestamp}] App: Online event fired, navigator.onLine=${navigator.onLine}`);

      toast.success("You are back online", {
        className: "custom-toast",
        autoClose: 2000,
      });

      // Small delay to ensure navigator.onLine is updated
      await new Promise(resolve => setTimeout(resolve, 100));

      console.log(`[${new Date().toISOString()}] App: After delay, navigator.onLine=${navigator.onLine}`);

      // Trigger sync of pending uploads and issues when back online
      // Use mobileUserDetails (MJWT user) if available, otherwise try regular userDetails
      const customerID = mobileUserDetails?.CustomerID || userDetails?.CustomerID;
      console.log(`[${new Date().toISOString()}] App: customerID=${customerID}, mobileUserDetails=${JSON.stringify(mobileUserDetails)}, userDetails=${JSON.stringify(userDetails)}`);

      if (customerID) {
        try {
          // Dynamic import to get the queue
          const { offlineUploadQueue } = await import("../utils/offlineUploadQueue");
          console.log(`[${new Date().toISOString()}] App: offlineUploadQueue loaded, starting sync for CustomerID=${customerID}`);

          const result = await offlineUploadQueue.syncAndWait(customerID, null);
          console.log(`[${new Date().toISOString()}] App: Sync complete - images: completed=${result.completed}, failed=${result.failed}; issues: submitted=${result.issuesSubmitted || 0}, failed=${result.issuesFailed || 0}`);
        } catch (err) {
          console.error(`[${new Date().toISOString()}] App: Sync failed:`, err);
        }
      } else {
        console.log(`[${new Date().toISOString()}] App: Skipping sync - no customerID available`);
      }
    };

    const handleOffline = () => {
      console.log(`[${new Date().toISOString()}] App: Offline event`);
      if (VRSConst.toastList.size >= VRSConst.MAX_TOAST) {
        toast.dismiss();
        VRSConst.toastList.clear();
      }
      if (VRSConst.toastList.size < VRSConst.MAX_TOAST) {
        const id = toast.info(
          "You are offline. Functionalities not available",
          {
            className: "custom-toast",
            onClose: () => {
              VRSConst.toastList.delete(id);
            },
          }
        );
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [mobileUserDetails, userDetails]);
  return (
    <div>
      {location.pathname === "/" && <Redirect to={VRSConst.mainPage} />}
      <Loader width={7} isLoading={loading} />
      {!loading && (
        <div>
          <ErrorBoundary>
            <SWUpdate />
            <HeaderList permission={permission} userDetails={userDetails} />
            <div className="container-fluid content-block">{children}</div>
            <Footer />
          </ErrorBoundary>
        </div>
      )}
      <ToastContainer position={toast.POSITION.BOTTOM_RIGHT} />
    </div>
  );
}

export default withRouter(App);

App.propTypes = {
  children: PropTypes.instanceOf(Array),
  location: PropTypes.objectOf(PropTypes.string),
};
App.defaultProps = {
  children: [],
  location: "",
};
